/**
 * 콘솔 셸 — 사이드바 + 상단 바 + 본문.
 *
 * 함대 요약과 긴급 오버라이드를 여기서 한 번만 폴링해 상단 바에 넘긴다. 화면마다
 * 따로 폴링하면 화면을 옮길 때마다 같은 질의가 새로 시작되고, 알림 숫자가 화면에
 * 따라 달라진다.
 *
 * **1024px 를 경계로 두 모양이다.** 넓으면 사이드바가 늘 떠 있고 본문이 그만큼 비켜
 * 선다(지금까지의 모습). 좁으면 사이드바는 서랍이 되고 본문이 폭을 다 쓴다 — 휴대폰에서
 * 사이드바가 256px 를 늘 차지하면 본문에 100px 남짓만 남는다.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import type { SignageOneClient, Me } from '../../api/client.js';
import type { Transport } from '../../api/transport.js';
import { useAsync, useMediaQuery, usePolling } from '../../hooks.js';
import { navCapabilities, titleFor } from '../../nav.js';
import { documentTitle, useTitleDetailFor } from '../pageTitle.js';
import { roleLabel } from '../../roles.js';
import { fillSiteLabels, resolveSiteLabels, type SiteLabels } from '../../siteLabels.js';
import { SiteLabelsContext } from '../siteLabelsContext.js';
import { Header, ALERT_ICONS, type Alert } from './Header.js';
import { readCollapsed, Sidebar, writeCollapsed } from './Sidebar.js';

/** 사이드바가 늘 떠 있는 폭. Tailwind 의 `lg` 와 같아야 한다 — 클래스와 이 값이 같은 경계를 본다. */
const WIDE_QUERY = '(min-width: 1024px)';

/** 본문을 꽉 채우는 화면 — 여백도 폭 묶음도 본문 스크롤도 없다(그 화면이 스스로 맡는다). */
const FULL_BLEED: ReadonlySet<string> = new Set(['/wall', '/integrated']);

export function AdminLayout({
  client,
  transport,
  me,
  onSignOut,
}: {
  client: SignageOneClient;
  transport: Transport;
  me: Me | null;
  onSignOut: () => void;
}): ReactNode {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const wide = useMediaQuery(WIDE_QUERY);
  /** 좁은 화면에서 메뉴 서랍이 열려 있는가. */
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  /*
   * 사업장 구성의 층 이름(지역 › 사업장 › 위치, 고객사가 고른 말) — 메뉴 부제와 사업장·모니터링 화면이 같은 말을
   * 쓰게 여기서 한 번 읽는다. 못 읽으면 기본 그대로 둔다(이름표일 뿐이라 화면을 막을 까닭이 없다).
   */
  const [siteLabels, setSiteLabels] = useState<SiteLabels>(() => resolveSiteLabels(null));
  const updateSiteLabels = useCallback((raw: Partial<SiteLabels> | null | undefined) => {
    setSiteLabels(resolveSiteLabels(raw));
  }, []);
  useEffect(() => {
    let alive = true;
    client
      .sites()
      .then((view) => {
        if (alive) updateSiteLabels(view.labels);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [client, updateSiteLabels]);
  const siteLabelsState = useMemo(
    () => ({ labels: siteLabels, update: updateSiteLabels }),
    [siteLabels, updateSiteLabels],
  );

  const { label, subtitle: subtitleTemplate } = titleFor(location.pathname);
  const subtitle = fillSiteLabels(subtitleTemplate, siteLabels);
  const fullBleed = FULL_BLEED.has(location.pathname);

  /*
   * 화면을 옮기면 서랍을 닫는다. 메뉴를 눌러 옮겨 간 사람이 서랍을 또 닫아야 본문이
   * 보이면, 휴대폰에서는 메뉴를 누를 때마다 두 번씩 누르게 된다. 넓어져도 닫는다 —
   * 창을 키운 뒤 다시 줄였을 때 서랍이 열린 채로 튀어나오지 않게.
   */
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, wide]);

  /*
   * 새로 들어간 화면은 맨 위에서 연다. 본문 상자(`main`)는 화면을 옮겨도 그대로라, 그냥 두면 앞 화면에서
   * 내려가 있던 만큼 새 화면도 내려간 채로 열린다. 메뉴·링크로 들어간 것(PUSH)만 올린다 — 뒤로 가기(POP)는
   * 목록이 보던 자리를 스스로 되돌리고(`useListReturn`), 필터를 바꾼 것(REPLACE)은 같은 자리에서 본다.
   */
  /* 탭 제목 — 메뉴 이름, 상세는 대상 이름까지(`pageTitle.ts`). */
  const titleDetail = useTitleDetailFor(location.pathname);
  useEffect(() => {
    document.title = documentTitle(label, titleDetail);
  }, [label, titleDetail]);

  const navigationType = useNavigationType();
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (navigationType === 'PUSH') mainRef.current?.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 새 기록 칸이 생길 때만.
  }, [location.key]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  const fleet = useAsync(() => client.fleetSummary(), [client]);
  const emergencies = useAsync(() => client.listEmergencies(), [client]);
  usePolling(fleet.reload, 30_000);
  usePolling(emergencies.reload, 30_000);

  const alerts = useMemo<Alert[] | null>(() => {
    if (!fleet.data) return null;
    // 담당 범위가 있으면 맡은 화면에 닿는 긴급만 — 남의 그룹에만 걸린 긴급까지 "작동 중" 으로 세지 않는다(2026-10-22).
    const active = emergencies.data?.items.filter((entry) => entry.active && entry.in_scope !== false).length ?? 0;
    return [
      {
        key: 'emergency',
        label: '긴급 오버라이드 작동 중',
        count: active,
        to: '/campaigns',
        tone: 'danger',
        icon: ALERT_ICONS.emergency,
      },
      {
        key: 'erroring',
        label: '오류를 보고한 화면',
        count: fleet.data.erroring,
        to: '/displays',
        tone: 'danger',
        icon: ALERT_ICONS.erroring,
      },
      {
        key: 'offline',
        label: '오프라인 화면',
        count: fleet.data.offline,
        /*
         * 센 것을 그대로 보여주는 목록으로 보낸다 — 거르지 않은 목록을 열면 "오프라인 3" 을
         * 눌러 놓고 전체에서 그 셋을 눈으로 찾아야 한다.
         *
         * 위의 `erroring` 은 그대로 둔다. 목록에 **오류만** 거르는 값이 없고, 가장 가까운
         * `attention`(오프라인이거나 오류)은 센 수보다 많은 줄을 내밀어 오히려 헷갈린다.
         */
        to: '/displays?status=offline',
        tone: 'warn',
        icon: ALERT_ICONS.offline,
      },
    ];
  }, [fleet.data, emergencies.data]);

  return (
    <SiteLabelsContext.Provider value={siteLabelsState}>
      <div className="h-viewport flex overflow-hidden bg-th-1">
        <Sidebar
          collapsed={collapsed && wide}
          onToggle={() => {
            const next = !collapsed;
            setCollapsed(next);
            writeCollapsed(next);
          }}
          drawer={!wide}
          open={navOpen}
          onClose={() => setNavOpen(false)}
          tenantLabel={me ? `${me.tenant_id} · ${roleLabel(me.role)}` : 'Digital Signage'}
          capabilities={me ? navCapabilities(me) : []}
        />

        <div
          className={`flex min-w-0 flex-1 flex-col transition-all duration-300 ease-in-out ${
            collapsed ? 'lg:ml-20' : 'lg:ml-64'
          }`}
        >
          <Header
            title={label}
            subtitle={subtitle}
            client={client}
            alerts={alerts}
            principal={me?.account?.name || me?.id || '연결 중…'}
            scope={me?.scope ? me.scope.groups.map((group) => group.name).join(', ') || '(지워진 그룹)' : null}
            transportKind={transport.kind}
            canSignOut={me?.auth === 'local'}
            onSignOut={onSignOut}
            onOpenNav={() => setNavOpen(true)}
          />

          {/*
          배경에 점 패턴을 깔지 않는다.

          프로토타입에는 있었지만 거기는 보여주기용 화면이었다. 실제 콘솔은 표와
          숫자가 빽빽한데, 그 뒤에서 패턴이 계속 움직이면 빈 곳이 오히려 눈에 띈다.
          데이터가 조밀할수록 바탕은 조용해야 한다.
        */}
          {/*
          모니터링은 **그림판이 본문을 꽉 채운다** — 여백·폭 묶음 없이, 스크롤도 본문이 아니라 그림판이 맡는다(끌어서 옮기고
          키우고 줄인다). 나머지 화면은 표와 글이라 폭을 묶는다.

          **스크롤은 `main` 하나다 — 그래서 `relative` 다.** 위치 기준이 없으면 본문 속 `absolute`(화면 낭독기용
          `sr-only` 글자 등)가 `body` 를 기준으로 본문 깊이에 놓여 `main` 에 잘리지 않고 문서 높이를 늘린다 — 창이 따로
          굴러, 설명서를 끝까지 내리면 휠이 창으로 넘어가 머리글·사이드바째 밀려 올라갔다(2026-10-26, 알림 상자가 있는
          설명서 16개 장).
        */}
          <main
            ref={mainRef}
            className={
              fullBleed
                ? 'relative flex-1 overflow-hidden bg-th-1'
                : 'relative flex-1 overflow-y-auto overflow-x-hidden bg-th-1 px-4 py-4 sm:px-6 sm:py-5'
            }
          >
            {/*
              폭을 묶는다 — 표 한 줄이 1500px 를 가로지르면 눈이 칸 사이를 헤맨다. 높이는 본문만큼 세워, 목록 화면의
              주 카드(`screen-fill`)가 비어 있어도 아래까지 채운다(styles.css).
            */}
            <div className={fullBleed ? 'h-full' : 'screen-host mx-auto max-w-[1280px]'}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </SiteLabelsContext.Provider>
  );
}
